import React, { useEffect, useMemo, useState } from 'react';
import { Download, Eye, FileText, History, Loader2, Plus, Trash2, X } from 'lucide-react';
import { useAuthStore, usePlayerStore } from '../stores';
import { apiClient } from '../utils/api';

interface GoalContribution {
  id: string;
  scorer: string;
  assistProvider: string;
  minute: string;
}

interface MatchReport {
  id: string;
  date: string;
  opponent: string;
  location: string;
  result: string;
  goalsFor: number;
  goalsAgainst: number;
  notes: string;
  goalContributions: GoalContribution[];
  stats: {
    possession?: string;
    shots?: number;
    shotsOnTarget?: number;
    corners?: number;
    fouls?: number;
    cards?: string;
    [key: string]: string | number | undefined;
  };
}

interface MatchReportRow {
  id: string;
  club_id: string;
  date: string;
  opponent: string;
  location: string | null;
  result: string | null;
  goals_for: number | null;
  goals_against: number | null;
  notes: string | null;
  stats: Record<string, unknown> | null;
  created_at?: string;
  updated_at?: string;
}

const createEmptyGoalContribution = (): GoalContribution => ({
  id: `goal-event-${Date.now()}-${Math.random().toString(36).slice(2, 8)}`,
  scorer: '',
  assistProvider: '',
  minute: '',
});

const createInitialReport = (): MatchReport => ({
  id: '',
  date: '',
  opponent: '',
  location: '',
  result: '',
  goalsFor: 0,
  goalsAgainst: 0,
  notes: '',
  goalContributions: [createEmptyGoalContribution()],
  stats: {},
});

const numberFields = new Set(['goalsFor', 'goalsAgainst']);
const statNumberFields = new Set(['shots', 'shotsOnTarget', 'corners', 'fouls']);

const formatStatLabel = (key: string) =>
  key
    .replace(/([A-Z])/g, ' $1')
    .replace(/^./, (letter) => letter.toUpperCase());

const formatDateLabel = (date: string) => {
  if (!date) return 'Not specified';

  const parsed = new Date(date);
  if (Number.isNaN(parsed.getTime())) return date;

  return parsed.toLocaleDateString('en-GB', {
    day: '2-digit',
    month: 'short',
    year: 'numeric',
  });
};

const escapeHtml = (value: string | number | undefined) => {
  const div = document.createElement('div');
  div.textContent = String(value ?? '');
  return div.innerHTML;
};

const normalizeGoalContributions = (value: unknown): GoalContribution[] => {
  if (!Array.isArray(value)) return [];

  return value.map((entry, index) => {
    const row = typeof entry === 'object' && entry ? (entry as Record<string, unknown>) : {};

    return {
      id: typeof row.id === 'string' && row.id ? row.id : `goal-event-${Date.now()}-${index}`,
      scorer: typeof row.scorer === 'string' ? row.scorer : '',
      assistProvider: typeof row.assistProvider === 'string' ? row.assistProvider : '',
      minute: typeof row.minute === 'string' ? row.minute : '',
    };
  });
};

const normalizeStats = (value: unknown): MatchReport['stats'] => {
  if (!value || typeof value !== 'object') return {};

  return Object.fromEntries(
    Object.entries(value as Record<string, unknown>).filter(
      ([, metricValue]) => typeof metricValue === 'string' || typeof metricValue === 'number'
    )
  ) as MatchReport['stats'];
};

const mapRowToReport = (row: MatchReportRow): MatchReport => {
  const rawStats = row.stats && typeof row.stats === 'object' ? row.stats : {};
  const statsPayload = rawStats as Record<string, unknown>;
  const statsSource =
    statsPayload.metrics && typeof statsPayload.metrics === 'object'
      ? statsPayload.metrics
      : Object.fromEntries(
        Object.entries(statsPayload).filter(([key]) => key !== 'goalContributions' && key !== 'metrics')
      );

  return {
    id: row.id,
    date: row.date || '',
    opponent: row.opponent || '',
    location: row.location || '',
    result: row.result || '',
    goalsFor: row.goals_for ?? 0,
    goalsAgainst: row.goals_against ?? 0,
    notes: row.notes || '',
    goalContributions: normalizeGoalContributions(statsPayload.goalContributions),
    stats: normalizeStats(statsSource),
  };
};

const prepareReport = (draft: MatchReport, id?: string): MatchReport => ({
  ...draft,
  id: id || draft.id || `match-report-${Date.now()}`,
  opponent: draft.opponent.trim(),
  location: draft.location.trim(),
  result: draft.result.trim(),
  notes: draft.notes.trim(),
  goalContributions: draft.goalContributions
    .filter((entry) => entry.scorer.trim() || entry.assistProvider.trim() || entry.minute.trim())
    .map((entry) => ({
      ...entry,
      scorer: entry.scorer.trim(),
      assistProvider: entry.assistProvider.trim(),
      minute: entry.minute.trim(),
    })),
  stats: Object.fromEntries(
    Object.entries(draft.stats).filter(([, value]) => value !== undefined && value !== '')
  ) as MatchReport['stats'],
});

const buildMatchReportPayload = (report: MatchReport, clubId: string) => ({
  club_id: clubId,
  date: report.date,
  opponent: report.opponent,
  location: report.location || null,
  result: report.result || null,
  goals_for: report.goalsFor,
  goals_against: report.goalsAgainst,
  notes: report.notes || null,
  stats: {
    metrics: report.stats,
    goalContributions: report.goalContributions,
  },
});

const getNarrativeSummary = (report: MatchReport) => {
  const resultLabel = report.result || `${report.goalsFor}-${report.goalsAgainst}`;
  const venue = report.location || 'an unspecified venue';
  const scorerNames = report.goalContributions
    .map((entry) => entry.scorer)
    .filter(Boolean)
    .join(', ');
  const possessionLine = report.stats.possession ? `The side recorded ${report.stats.possession} possession.` : '';
  const shootingLine =
    report.stats.shots !== undefined || report.stats.shotsOnTarget !== undefined
      ? `The team produced ${report.stats.shots ?? 0} shots with ${report.stats.shotsOnTarget ?? 0} on target.`
      : '';
  const scorerLine = scorerNames
    ? `Goals were supplied by ${scorerNames}.`
    : 'No detailed goalscorer timeline was recorded.';

  return `On ${formatDateLabel(report.date)}, the team faced ${report.opponent || 'an unnamed opponent'} at ${venue} and finished with a ${resultLabel} outcome. ${scorerLine} ${possessionLine} ${shootingLine}`.trim();
};

const buildPrintableHtml = (report: MatchReport) => {
  const statsRows = Object.entries(report.stats)
    .map(
      ([key, value]) => `
        <tr>
          <td>${escapeHtml(formatStatLabel(key))}</td>
          <td>${escapeHtml(value)}</td>
        </tr>
      `
    )
    .join('');

  const goalRows = report.goalContributions.length
    ? report.goalContributions
      .map(
        (entry, index) => `
            <tr>
              <td>${index + 1}</td>
              <td>${escapeHtml(entry.minute || '-')}</td>
              <td>${escapeHtml(entry.scorer || '-')}</td>
              <td>${escapeHtml(entry.assistProvider || 'Unassisted')}</td>
            </tr>
          `
      )
      .join('')
    : `
        <tr>
          <td colspan="4">No goal contributions recorded.</td>
        </tr>
      `;

  return `
    <!doctype html>
    <html>
      <head>
        <meta charset="utf-8" />
        <title>Match Report - ${escapeHtml(report.opponent)}</title>
        <style>
          body {
            font-family: Arial, Helvetica, sans-serif;
            background: #ffffff;
            color: #111827;
            margin: 0;
            padding: 32px;
          }
          .wrap {
            max-width: 920px;
            margin: 0 auto;
          }
          .header {
            border-bottom: 3px solid #22c55e;
            padding-bottom: 16px;
            margin-bottom: 24px;
          }
          .eyebrow {
            color: #16a34a;
            font-size: 12px;
            font-weight: 700;
            text-transform: uppercase;
            letter-spacing: 0.12em;
            margin-bottom: 8px;
          }
          h1, h2 {
            margin: 0 0 12px;
          }
          .meta {
            display: grid;
            grid-template-columns: repeat(2, minmax(0, 1fr));
            gap: 12px;
          }
          .card {
            background: #f8fafc;
            border: 1px solid #e5e7eb;
            border-radius: 12px;
            padding: 16px;
            margin-bottom: 20px;
          }
          .label {
            color: #6b7280;
            font-size: 12px;
            font-weight: 700;
            text-transform: uppercase;
            letter-spacing: 0.08em;
            margin-bottom: 6px;
          }
          .value {
            font-size: 16px;
            font-weight: 700;
          }
          p {
            line-height: 1.6;
          }
          table {
            width: 100%;
            border-collapse: collapse;
          }
          th, td {
            border: 1px solid #d1d5db;
            padding: 10px 12px;
            text-align: left;
            font-size: 14px;
          }
          th {
            background: #f3f4f6;
          }
          @media print {
            body {
              padding: 16px;
            }
          }
        </style>
      </head>
      <body>
        <div class="wrap">
          <div class="header">
            <div class="eyebrow">Scoutang Match Report</div>
            <h1>${escapeHtml(report.opponent || 'Match Report')}</h1>
            <p>${escapeHtml(getNarrativeSummary(report))}</p>
          </div>

          <div class="meta">
            <div class="card">
              <div class="label">Date</div>
              <div class="value">${escapeHtml(formatDateLabel(report.date))}</div>
            </div>
            <div class="card">
              <div class="label">Venue</div>
              <div class="value">${escapeHtml(report.location || 'Not specified')}</div>
            </div>
            <div class="card">
              <div class="label">Final Result</div>
              <div class="value">${escapeHtml(report.result || `${report.goalsFor}-${report.goalsAgainst}`)}</div>
            </div>
            <div class="card">
              <div class="label">Scoreline</div>
              <div class="value">${escapeHtml(`${report.goalsFor} - ${report.goalsAgainst}`)}</div>
            </div>
          </div>

          <div class="card">
            <h2>Goal Contributions Timeline</h2>
            <table>
              <thead>
                <tr>
                  <th>#</th>
                  <th>Minute</th>
                  <th>Goalscorer</th>
                  <th>Assist Provider</th>
                </tr>
              </thead>
              <tbody>
                ${goalRows}
              </tbody>
            </table>
          </div>

          <div class="card">
            <h2>Statistical Breakdown</h2>
            <table>
              <thead>
                <tr>
                  <th>Metric</th>
                  <th>Value</th>
                </tr>
              </thead>
              <tbody>
                ${statsRows ||
    `
                    <tr>
                      <td colspan="2">No match stats recorded.</td>
                    </tr>
                  `
    }
              </tbody>
            </table>
          </div>

          <div class="card">
            <h2>Coach / Scout Notes</h2>
            <p>${escapeHtml(report.notes || 'No additional notes provided.')}</p>
          </div>
        </div>
      </body>
    </html>
  `;
};

const exportReportAsPdf = (report: MatchReport) => {
  const printWindow = window.open('', '_blank', 'width=1000,height=800');

  if (!printWindow) {
    window.alert('Please allow pop-ups to preview and export the PDF.');
    return;
  }

  printWindow.document.open();
  printWindow.document.write(buildPrintableHtml(report));
  printWindow.document.close();
  printWindow.focus();

  setTimeout(() => {
    printWindow.print();
  }, 300);
};

const MatchReportPreview: React.FC<{
  report: MatchReport;
  title: string;
  onClose: () => void;
  onExportPdf: () => void;
}> = ({ report, title, onClose, onExportPdf }) => {
  const statsEntries = Object.entries(report.stats);
  const resultLabel = report.result || `${report.goalsFor}-${report.goalsAgainst}`;

  return (
    <div className="fixed inset-0 z-50 bg-black/80 backdrop-blur-sm p-4 md:p-8 overflow-y-auto">
      <div className="max-w-5xl mx-auto bg-[#07111a] border border-white/10 rounded-3xl shadow-2xl overflow-hidden">
        <div className="flex items-center justify-between gap-4 px-6 md:px-8 py-5 border-b border-white/10 bg-white/5">
          <div>
            <p className="text-[11px] uppercase tracking-[0.2em] font-black text-green-400 mb-1">{title}</p>
            <h3 className="text-2xl font-black text-white">Detailed Match Report Preview</h3>
          </div>
          <div className="flex items-center gap-3">
            <button
              type="button"
              onClick={onExportPdf}
              className="inline-flex items-center gap-2 px-4 py-2 rounded-xl bg-green-500 hover:bg-green-600 text-white font-black text-sm"
            >
              <Download className="w-4 h-4" />
              Export as PDF
            </button>
            <button
              type="button"
              onClick={onClose}
              className="inline-flex items-center justify-center w-10 h-10 rounded-xl bg-white/5 hover:bg-white/10 text-white"
            >
              <X className="w-5 h-5" />
            </button>
          </div>
        </div>

        <div className="p-6 md:p-8 space-y-6">
          <section className="rounded-2xl border border-white/10 bg-white/5 p-6">
            <p className="text-[11px] uppercase tracking-[0.2em] font-black text-green-400 mb-2">Executive Summary</p>
            <h4 className="text-3xl font-black text-white mb-3">
              {report.opponent || 'Unnamed Opponent'} — {resultLabel}
            </h4>
            <p className="text-gray-300 leading-7">{getNarrativeSummary(report)}</p>
          </section>

          <section className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-4 gap-4">
            {[
              { label: 'Date', value: formatDateLabel(report.date) },
              { label: 'Venue', value: report.location || 'Not specified' },
              { label: 'Result', value: resultLabel },
              { label: 'Scoreline', value: `${report.goalsFor} - ${report.goalsAgainst}` },
            ].map((item) => (
              <div key={item.label} className="rounded-2xl border border-white/10 bg-[#0b1622] p-5">
                <p className="text-[10px] uppercase tracking-[0.16em] font-black text-gray-500 mb-2">{item.label}</p>
                <p className="text-lg font-black text-white">{item.value}</p>
              </div>
            ))}
          </section>

          <section className="rounded-2xl border border-white/10 bg-white/5 p-6">
            <div className="flex items-center gap-2 mb-4">
              <FileText className="w-5 h-5 text-green-400" />
              <h4 className="text-xl font-black text-white">Goal Contributions Timeline</h4>
            </div>

            {report.goalContributions.length ? (
              <div className="overflow-x-auto">
                <table className="w-full text-left">
                  <thead>
                    <tr className="border-b border-white/10 text-[11px] uppercase tracking-[0.16em] text-gray-500">
                      <th className="py-3 pr-4">#</th>
                      <th className="py-3 pr-4">Minute</th>
                      <th className="py-3 pr-4">Goalscorer</th>
                      <th className="py-3">Assist Provider</th>
                    </tr>
                  </thead>
                  <tbody>
                    {report.goalContributions.map((entry, index) => (
                      <tr key={entry.id} className="border-b border-white/5">
                        <td className="py-4 pr-4 text-white font-bold">{index + 1}</td>
                        <td className="py-4 pr-4 text-green-400 font-bold">{entry.minute || '-'}</td>
                        <td className="py-4 pr-4 text-white">{entry.scorer || '-'}</td>
                        <td className="py-4 text-gray-300">{entry.assistProvider || 'Unassisted'}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            ) : (
              <p className="text-gray-400">No goalscorer or assist timeline recorded.</p>
            )}
          </section>

          <section className="rounded-2xl border border-white/10 bg-white/5 p-6">
            <h4 className="text-xl font-black text-white mb-4">Statistical Breakdown</h4>

            {statsEntries.length ? (
              <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-4">
                {statsEntries.map(([key, value]) => (
                  <div key={key} className="rounded-2xl border border-white/10 bg-[#0b1622] p-5">
                    <p className="text-[10px] uppercase tracking-[0.16em] font-black text-gray-500 mb-2">
                      {formatStatLabel(key)}
                    </p>
                    <p className="text-2xl font-black text-white">{value}</p>
                  </div>
                ))}
              </div>
            ) : (
              <p className="text-gray-400">No stat metrics have been recorded for this match yet.</p>
            )}
          </section>

          <section className="rounded-2xl border border-white/10 bg-white/5 p-6">
            <h4 className="text-xl font-black text-white mb-4">Detailed Notes</h4>
            <div className="rounded-2xl bg-[#0b1622] border border-white/10 p-5">
              <p className="text-gray-300 whitespace-pre-wrap leading-7">
                {report.notes || 'No match notes provided.'}
              </p>
            </div>
          </section>
        </div>
      </div>
    </div>
  );
};

const MatchReportGenerator: React.FC = () => {
  const { user } = useAuthStore();
  const { clubs, fetchClubs, players, fetchPlayers } = usePlayerStore();

  const [report, setReport] = useState<MatchReport>(() => createInitialReport());
  const [saved, setSaved] = useState(false);
  const [savedReports, setSavedReports] = useState<MatchReport[]>([]);
  const [previewContext, setPreviewContext] = useState<{ report: MatchReport; title: string } | null>(null);
  const [isLoadingReports, setIsLoadingReports] = useState(false);
  const [isSavingReport, setIsSavingReport] = useState(false);
  const [deletingReportId, setDeletingReportId] = useState<string | null>(null);
  const [persistenceError, setPersistenceError] = useState('');

  useEffect(() => {
    if (!clubs.length) {
      void fetchClubs();
    }
  }, [clubs.length, fetchClubs]);

  useEffect(() => {
    if (!players.length) {
      void fetchPlayers();
    }
  }, [fetchPlayers, players.length]);

  const resolvedClub = useMemo(() => {
    const clubLookup = (user?.org || user?.name || '').trim().toLowerCase();

    if (user?.role === 'club') {
      return (
        clubs.find((club) => club.id === user.id) ||
        clubs.find((club) => club.name.trim().toLowerCase() === clubLookup) ||
        null
      );
    }

    if (clubLookup) {
      return clubs.find((club) => club.name.trim().toLowerCase() === clubLookup) || null;
    }

    return null;
  }, [clubs, user?.id, user?.name, user?.org, user?.role]);

  const resolvedClubId = resolvedClub?.id || (user?.role === 'club' ? user.id : '');
  const resolvedClubName = resolvedClub?.name || user?.org || user?.name || 'your club';

  const availablePlayers = useMemo(() => {
    if (!players.length) return [];

    if (user?.role === 'admin') return players;

    if (user?.role === 'club') {
      const normalizedClubName = resolvedClub?.name?.trim().toLowerCase();
      return players.filter((player) =>
        player.owner_club_id === resolvedClubId ||
        Boolean(normalizedClubName && player.club?.trim().toLowerCase() === normalizedClubName)
      );
    }

    if (!user?.email) return [];
    return players.filter((player) => player.scout === user.email);
  }, [players, resolvedClub?.name, resolvedClubId, user?.email, user?.role]);

  const updatePlayerStats = async (goalContributions: GoalContribution[]) => {
    if (!resolvedClubId) return;

    const playerStats: Record<string, { goals: number; assists: number }> = {};

    // Calculate stats from goal contributions
    goalContributions.forEach((contribution) => {
      if (contribution.scorer && contribution.scorer !== 'Unassisted') {
        if (!playerStats[contribution.scorer]) {
          playerStats[contribution.scorer] = { goals: 0, assists: 0 };
        }
        playerStats[contribution.scorer].goals += 1;
      }

      if (contribution.assistProvider && contribution.assistProvider !== 'Unassisted') {
        if (!playerStats[contribution.assistProvider]) {
          playerStats[contribution.assistProvider] = { goals: 0, assists: 0 };
        }
        playerStats[contribution.assistProvider].assists += 1;
      }
    });

    // Update player stats in database
    for (const [playerName, stats] of Object.entries(playerStats)) {
      const player = availablePlayers.find((p) => p.name === playerName);
      if (player) {
        try {
          await apiClient.updatePlayer(player.id, {
            goals: player.goals + stats.goals,
            assists: player.assists + stats.assists,
          });
        } catch (error) {
          console.error(`Failed to update stats for ${playerName}:`, error);
        }
      }
    }
  };

  useEffect(() => {
    let isCancelled = false;

    const loadReports = async () => {
      if (!resolvedClubId) {
        setSavedReports([]);
        return;
      }

      setIsLoadingReports(true);
      setPersistenceError('');

      let data;
      let error = null;

      try {
        data = await apiClient.getMatchReports(resolvedClubId);
      } catch (err) {
        error = err;
      }

      if (isCancelled) return;

      if (error) {
        console.error('Unable to load match reports from database:', error);
        setPersistenceError(`Unable to load reports from database: ${error instanceof Error ? error.message : String(error)}`);
        setSavedReports([]);
      } else {
        setSavedReports(((data as MatchReportRow[] | null) || []).map(mapRowToReport));
      }

      setIsLoadingReports(false);
    };

    void loadReports();

    return () => {
      isCancelled = true;
    };
  }, [resolvedClubId]);

  const totalReports = useMemo(() => savedReports.length, [savedReports.length]);

  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    const { name, value } = e.target;

    setReport((prev) => ({
      ...prev,
      [name]: numberFields.has(name) ? Number(value) || 0 : value,
    }));
  };

  const handleStatsChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value } = e.target;

    setReport((prev) => ({
      ...prev,
      stats: {
        ...prev.stats,
        [name]: statNumberFields.has(name) ? (value === '' ? undefined : Number(value)) : value,
      },
    }));
  };

  const handleGoalContributionChange = (
    id: string,
    field: keyof GoalContribution,
    value: string
  ) => {
    setReport((prev) => ({
      ...prev,
      goalContributions: prev.goalContributions.map((entry) =>
        entry.id === id ? { ...entry, [field]: value } : entry
      ),
    }));
  };

  const handleAddGoalContribution = () => {
    setReport((prev) => ({
      ...prev,
      goalContributions: [...prev.goalContributions, createEmptyGoalContribution()],
    }));
  };

  const handleRemoveGoalContribution = (id: string) => {
    setReport((prev) => ({
      ...prev,
      goalContributions:
        prev.goalContributions.length === 1
          ? [createEmptyGoalContribution()]
          : prev.goalContributions.filter((entry) => entry.id !== id),
    }));
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!resolvedClubId) {
      setPersistenceError('Unable to determine which club should own this report. Sign in as a club account or load the club context first.');
      return;
    }

    const preparedReport = prepareReport(report);
    setIsSavingReport(true);
    setPersistenceError('');

    let data;
    let error = null;

    try {
      data = await apiClient.createMatchReport(buildMatchReportPayload(preparedReport, resolvedClubId));
    } catch (err) {
      error = err;
    }

    if (error) {
      console.error('Unable to save match report to database:', error);
      setPersistenceError(`Unable to save report to database: ${error instanceof Error ? error.message : String(error)}`);
      setIsSavingReport(false);
      return;
    }

    // Update player stats after successful report creation
    await updatePlayerStats(preparedReport.goalContributions);

    const persistedReport = mapRowToReport(data as MatchReportRow);

    setSavedReports((prev) => [persistedReport, ...prev]);
    setSaved(true);
    setReport(createInitialReport());
    setIsSavingReport(false);

    window.setTimeout(() => setSaved(false), 2000);
  };

  const handleDeleteReport = async (id: string) => {
    setDeletingReportId(id);
    setPersistenceError('');

    // Note: The API client doesn't have a delete method for match reports yet
    // This would need to be implemented in the API client
    const error = new Error('Delete functionality not implemented in API client');

    if (error) {
      console.error('Unable to delete match report from database:', error);
      setPersistenceError(`Unable to delete report from database: ${error.message}`);
      setDeletingReportId(null);
      return;
    }

    setSavedReports((prev) => prev.filter((savedReport) => savedReport.id !== id));
    setDeletingReportId(null);
  };

  const handlePreviewDraft = () => {
    setPreviewContext({
      report: prepareReport(report, report.id || `draft-report-${Date.now()}`),
      title: 'Draft Report Preview',
    });
  };

  const handlePreviewSavedReport = (savedReport: MatchReport) => {
    setPreviewContext({
      report: savedReport,
      title: 'Saved Report Preview',
    });
  };

  const handleExportAllReports = () => {
    const blob = new Blob([JSON.stringify(savedReports, null, 2)], {
      type: 'application/json;charset=utf-8',
    });
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');

    link.href = url;
    link.download = `${resolvedClubName.replace(/\s+/g, '-').toLowerCase()}-match-reports.json`;
    link.click();

    URL.revokeObjectURL(url);
  };

  const saveDisabled = !resolvedClubId || isSavingReport;

  return (
    <>
      <div className="max-w-7xl mx-auto grid grid-cols-1 xl:grid-cols-[380px_minmax(0,1fr)] gap-8 items-start">
        <aside className="bg-white/5 border border-white/10 rounded-2xl shadow-lg p-6 xl:sticky xl:top-24">
          <div className="flex items-start justify-between gap-4 mb-4">
            <div>
              <div className="flex items-center gap-2 mb-2">
                <History className="w-5 h-5 text-green-400" />
                <h3 className="text-xl font-black text-white">Recorded Reports</h3>
              </div>
              <p className="text-sm text-gray-400">
                {totalReports} {totalReports === 1 ? 'report' : 'reports'} saved in database
              </p>
            </div>
            <button
              type="button"
              onClick={handleExportAllReports}
              disabled={!savedReports.length}
              className="inline-flex items-center gap-2 px-3 py-2 rounded-lg bg-green-500 hover:bg-green-600 disabled:opacity-50 disabled:cursor-not-allowed text-white text-xs font-black"
            >
              <Download className="w-4 h-4" />
              Export All
            </button>
          </div>

          <div className="mb-4 rounded-xl border border-green-500/20 bg-green-500/10 p-3">
            <p className="text-[11px] uppercase tracking-[0.16em] font-black text-green-300 mb-1">Storage Target</p>
            <p className="text-sm text-green-100">
              Reports are now loaded from and saved to secure storage for <span className="font-black">{resolvedClubName}</span>.
            </p>
          </div>

          {!resolvedClubId && (
            <div className="mb-4 rounded-xl border border-yellow-500/20 bg-yellow-500/10 p-3">
              <p className="text-sm text-yellow-200">
                No club context is active, so reports cannot be saved yet.
              </p>
            </div>
          )}

          {persistenceError && (
            <div className="mb-4 rounded-xl border border-red-500/20 bg-red-500/10 p-3">
              <p className="text-sm text-red-200">{persistenceError}</p>
            </div>
          )}

          <div className="space-y-4 max-h-[760px] overflow-y-auto pr-1">
            {isLoadingReports ? (
              <div className="rounded-xl border border-white/10 bg-gray-900/40 p-6 text-center">
                <Loader2 className="w-8 h-8 text-green-400 mx-auto mb-3 animate-spin" />
                <p className="text-sm font-semibold text-gray-300">Loading reports from database...</p>
              </div>
            ) : !savedReports.length ? (
              <div className="rounded-xl border border-dashed border-white/10 bg-gray-900/40 p-6 text-center">
                <FileText className="w-10 h-10 text-gray-500 mx-auto mb-3" />
                <p className="text-sm font-semibold text-gray-300">No match reports recorded yet.</p>
                <p className="text-xs text-gray-500 mt-1">Saved reports will appear here with preview and PDF export actions.</p>
              </div>
            ) : (
              savedReports.map((savedReport) => (
                <div
                  key={savedReport.id}
                  className="rounded-xl border border-white/10 bg-gray-900/50 p-4 space-y-3"
                >
                  <div className="flex items-start justify-between gap-3">
                    <div>
                      <p className="text-sm font-black text-white">{savedReport.opponent || 'Unnamed Opponent'}</p>
                      <p className="text-xs text-gray-400">{formatDateLabel(savedReport.date)}</p>
                    </div>
                    <span className="text-xs font-bold px-2 py-1 rounded-full bg-green-500/15 text-green-300">
                      {savedReport.result || `${savedReport.goalsFor}-${savedReport.goalsAgainst}`}
                    </span>
                  </div>

                  <div className="text-xs text-gray-400 space-y-1">
                    <p>
                      <span className="text-gray-500">Venue:</span> {savedReport.location || 'Not specified'}
                    </p>
                    <p>
                      <span className="text-gray-500">Goals logged:</span> {savedReport.goalContributions.length}
                    </p>
                  </div>

                  <div className="grid grid-cols-2 gap-2 pt-1">
                    <button
                      type="button"
                      onClick={() => handlePreviewSavedReport(savedReport)}
                      className="inline-flex items-center justify-center gap-2 px-3 py-2 rounded-lg bg-blue-500/15 hover:bg-blue-500/25 border border-blue-400/20 text-blue-300 text-xs font-black"
                    >
                      <Eye className="w-4 h-4" />
                      Preview
                    </button>
                    <button
                      type="button"
                      onClick={() => exportReportAsPdf(savedReport)}
                      className="inline-flex items-center justify-center gap-2 px-3 py-2 rounded-lg bg-green-500/15 hover:bg-green-500/25 border border-green-400/20 text-green-300 text-xs font-black"
                    >
                      <Download className="w-4 h-4" />
                      PDF
                    </button>
                    <button
                      type="button"
                      onClick={() => handleDeleteReport(savedReport.id)}
                      disabled={deletingReportId === savedReport.id}
                      className="col-span-2 inline-flex items-center justify-center gap-2 px-3 py-2 rounded-lg bg-red-500/10 hover:bg-red-500/20 border border-red-400/20 text-red-300 text-xs font-black disabled:opacity-50"
                    >
                      {deletingReportId === savedReport.id ? (
                        <Loader2 className="w-4 h-4 animate-spin" />
                      ) : (
                        <Trash2 className="w-4 h-4" />
                      )}
                      Delete Report
                    </button>
                  </div>
                </div>
              ))
            )}
          </div>
        </aside>

        <div className="bg-white/5 p-8 rounded-2xl border border-white/10 shadow-lg">
          <h2 className="text-2xl font-black text-green-400 mb-2">Match Report Generator</h2>
          <p className="text-sm text-gray-400 mb-2">
            Build a more detailed report with scoreline, goalscorers, assist providers, goal times, match stats, and notes. Preview the full report before exporting it as PDF.
          </p>
          <p className="text-xs text-green-300 mb-6 uppercase tracking-[0.16em] font-black">
            Database sync: {resolvedClubId ? `active for ${resolvedClubName}` : 'waiting for club context'}
          </p>

          <form onSubmit={handleSubmit} className="space-y-8">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <div>
                <label className="block text-xs font-bold text-gray-400 mb-1">Date</label>
                <input
                  type="date"
                  name="date"
                  value={report.date}
                  onChange={handleChange}
                  className="w-full px-4 py-2 rounded bg-gray-900 text-white"
                  required
                />
              </div>
              <div>
                <label className="block text-xs font-bold text-gray-400 mb-1">Opponent</label>
                <input
                  type="text"
                  name="opponent"
                  value={report.opponent}
                  onChange={handleChange}
                  className="w-full px-4 py-2 rounded bg-gray-900 text-white"
                  required
                />
              </div>
              <div>
                <label className="block text-xs font-bold text-gray-400 mb-1">Location</label>
                <input
                  type="text"
                  name="location"
                  value={report.location}
                  onChange={handleChange}
                  className="w-full px-4 py-2 rounded bg-gray-900 text-white"
                  placeholder="e.g. Onikan Stadium"
                />
              </div>
              <div>
                <label className="block text-xs font-bold text-gray-400 mb-1">Result</label>
                <input
                  type="text"
                  name="result"
                  value={report.result}
                  onChange={handleChange}
                  className="w-full px-4 py-2 rounded bg-gray-900 text-white"
                  placeholder="e.g. 2-1 Win"
                />
              </div>
            </div>

            <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
              <div>
                <label className="block text-xs font-bold text-gray-400 mb-1">Goals For</label>
                <input
                  type="number"
                  name="goalsFor"
                  value={report.goalsFor}
                  onChange={handleChange}
                  className="w-full px-4 py-2 rounded bg-gray-900 text-white"
                />
              </div>
              <div>
                <label className="block text-xs font-bold text-gray-400 mb-1">Goals Against</label>
                <input
                  type="number"
                  name="goalsAgainst"
                  value={report.goalsAgainst}
                  onChange={handleChange}
                  className="w-full px-4 py-2 rounded bg-gray-900 text-white"
                />
              </div>
              <div>
                <label className="block text-xs font-bold text-gray-400 mb-1">Possession</label>
                <input
                  type="text"
                  name="possession"
                  value={report.stats.possession || ''}
                  onChange={handleStatsChange}
                  className="w-full px-4 py-2 rounded bg-gray-900 text-white"
                  placeholder="e.g. 55%"
                />
              </div>
              <div>
                <label className="block text-xs font-bold text-gray-400 mb-1">Shots</label>
                <input
                  type="number"
                  name="shots"
                  value={report.stats.shots ?? ''}
                  onChange={handleStatsChange}
                  className="w-full px-4 py-2 rounded bg-gray-900 text-white"
                />
              </div>
              <div>
                <label className="block text-xs font-bold text-gray-400 mb-1">Shots on Target</label>
                <input
                  type="number"
                  name="shotsOnTarget"
                  value={report.stats.shotsOnTarget ?? ''}
                  onChange={handleStatsChange}
                  className="w-full px-4 py-2 rounded bg-gray-900 text-white"
                />
              </div>
              <div>
                <label className="block text-xs font-bold text-gray-400 mb-1">Corners</label>
                <input
                  type="number"
                  name="corners"
                  value={report.stats.corners ?? ''}
                  onChange={handleStatsChange}
                  className="w-full px-4 py-2 rounded bg-gray-900 text-white"
                />
              </div>
              <div>
                <label className="block text-xs font-bold text-gray-400 mb-1">Fouls</label>
                <input
                  type="number"
                  name="fouls"
                  value={report.stats.fouls ?? ''}
                  onChange={handleStatsChange}
                  className="w-full px-4 py-2 rounded bg-gray-900 text-white"
                />
              </div>
              <div>
                <label className="block text-xs font-bold text-gray-400 mb-1">Cards</label>
                <input
                  type="text"
                  name="cards"
                  value={report.stats.cards || ''}
                  onChange={handleStatsChange}
                  className="w-full px-4 py-2 rounded bg-gray-900 text-white"
                  placeholder="e.g. 2Y, 1R"
                />
              </div>
            </div>

            <div className="rounded-2xl border border-white/10 bg-[#08121b] p-5">
              <div className="flex items-center justify-between gap-4 mb-4">
                <div>
                  <h3 className="text-lg font-black text-white">Goal Contributions</h3>
                  <p className="text-sm text-gray-400">
                    Record the scorer, assist provider, and minute for each goal.
                  </p>
                </div>
                <button
                  type="button"
                  onClick={handleAddGoalContribution}
                  className="inline-flex items-center gap-2 px-4 py-2 rounded-xl bg-green-500/15 hover:bg-green-500/25 border border-green-400/20 text-green-300 text-sm font-black"
                >
                  <Plus className="w-4 h-4" />
                  Add Goal Event
                </button>
              </div>

              <div className="space-y-4">
                {report.goalContributions.map((entry, index) => (
                  <div
                    key={entry.id}
                    className="grid grid-cols-1 md:grid-cols-[1.1fr_1.1fr_180px_auto] gap-3 items-end rounded-2xl border border-white/10 bg-white/5 p-4"
                  >
                    <div>
                      <label className="block text-xs font-bold text-gray-400 mb-1">
                        Goalscorer #{index + 1}
                      </label>
                      <select
                        value={entry.scorer}
                        onChange={(e) => handleGoalContributionChange(entry.id, 'scorer', e.target.value)}
                        className="w-full px-4 py-2 rounded bg-gray-900 text-white"
                      >
                        <option value="">Select player...</option>
                        {availablePlayers.map((player) => (
                          <option key={player.id} value={player.name}>
                            {player.name}
                          </option>
                        ))}
                      </select>
                    </div>
                    <div>
                      <label className="block text-xs font-bold text-gray-400 mb-1">Assist Provider</label>
                      <select
                        value={entry.assistProvider}
                        onChange={(e) => handleGoalContributionChange(entry.id, 'assistProvider', e.target.value)}
                        className="w-full px-4 py-2 rounded bg-gray-900 text-white"
                      >
                        <option value="">Select player...</option>
                        <option value="Unassisted">Unassisted</option>
                        {availablePlayers.map((player) => (
                          <option key={player.id} value={player.name}>
                            {player.name}
                          </option>
                        ))}
                      </select>
                    </div>
                    <div>
                      <label className="block text-xs font-bold text-gray-400 mb-1">Goal Time</label>
                      <input
                        type="text"
                        value={entry.minute}
                        onChange={(e) => handleGoalContributionChange(entry.id, 'minute', e.target.value)}
                        className="w-full px-4 py-2 rounded bg-gray-900 text-white"
                        placeholder="e.g. 12', 45+1'"
                      />
                    </div>
                    <button
                      type="button"
                      onClick={() => handleRemoveGoalContribution(entry.id)}
                      className="inline-flex items-center justify-center px-4 py-2 rounded-xl bg-red-500/10 hover:bg-red-500/20 border border-red-400/20 text-red-300 font-black text-sm"
                    >
                      <Trash2 className="w-4 h-4" />
                    </button>
                  </div>
                ))}
              </div>
            </div>

            <div>
              <label className="block text-xs font-bold text-gray-400 mb-1">Detailed Match Notes</label>
              <textarea
                name="notes"
                value={report.notes}
                onChange={handleChange}
                className="w-full px-4 py-3 rounded bg-gray-900 text-white"
                rows={6}
                placeholder="Add tactical observations, standout performers, turning points, substitutions, defensive shape, set-piece details, and overall match context..."
              />
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <button
                type="button"
                onClick={handlePreviewDraft}
                className="w-full py-3 bg-blue-500/15 hover:bg-blue-500/25 border border-blue-400/20 text-blue-300 font-black rounded-xl"
              >
                Preview Full Report
              </button>

              <button
                type="submit"
                disabled={saveDisabled}
                className="w-full py-3 bg-green-500 hover:bg-green-600 disabled:opacity-50 disabled:cursor-not-allowed text-white font-black rounded-xl inline-flex items-center justify-center gap-2"
              >
                {isSavingReport ? <Loader2 className="w-4 h-4 animate-spin" /> : null}
                {isSavingReport ? 'Saving to database...' : 'Save Match Report'}
              </button>
            </div>

            {saved && <div className="text-green-400 text-center font-bold mt-2">Report saved to database.</div>}
          </form>
        </div>
      </div>

      {previewContext && (
        <MatchReportPreview
          report={previewContext.report}
          title={previewContext.title}
          onClose={() => setPreviewContext(null)}
          onExportPdf={() => exportReportAsPdf(previewContext.report)}
        />
      )}
    </>
  );
};

export default MatchReportGenerator;
